import { HttpService } from 'core/services/http';
import { Utils } from 'core/utils';
import React from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Chip,
    Button
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';

const AVATAR_COLORS = [
    '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828',
    '#00796b', '#5d4037', '#0288d1', '#512da8', '#d81b60'
];

function nameToColor(name) {
    let hash = 0;
    for (let i = 0; i < (name?.length || 0); i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function CompanyItem({ company }) {
    const jobsCount = company?.jobs_count ?? 0;
    const jobLabel = `${jobsCount} open job${jobsCount === 1 ? '' : 's'}`;
    const locationLabel = [company?.city, company?.country?.title ?? company?.country?.name]
        .filter(Boolean)
        .join(', ') || null;

    const getCompanyLogo = () => {
        if (company?.image) {
            if (company.image.startsWith('http')) return company.image;
            return `${HttpService.SiteBase}${company.image}`;
        }
        return null;
    };

    const companyTitle = Utils.normalizeText
        ? Utils.normalizeText(company.title)
        : company.title;

    const logoUrl = getCompanyLogo();
    const initial = companyTitle?.charAt(0)?.toUpperCase() || '?';

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 4,
                '&:hover': {
                    borderColor: '#cdd4ea',
                    boxShadow: '0 14px 34px -16px rgba(74,91,176,.35)',
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                {/* Logo + name row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Avatar
                        variant="rounded"
                        src={logoUrl || undefined}
                        alt={companyTitle}
                        sx={{
                            width: 48,
                            height: 48,
                            flexShrink: 0,
                            bgcolor: logoUrl ? 'grey.100' : nameToColor(companyTitle),
                            border: '1px solid',
                            borderColor: 'divider',
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#fff',
                            '& .MuiAvatar-img': {
                                objectFit: 'contain',
                                objectPosition: 'center'
                            }
                        }}
                    >
                        {initial}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            component={Link}
                            to={`/employer/${company.id}`}
                            variant="subtitle1"
                            fontWeight={600}
                            sx={{
                                display: 'block',
                                color: 'text.primary',
                                textDecoration: 'none',
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&:hover': { color: 'primary.main' }
                            }}
                        >
                            {companyTitle}
                        </Typography>
                        {company.industry?.title && (
                            <Typography variant="caption" color="text.secondary">
                                {company.industry.title}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Meta row */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {locationLabel && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <LocationOnIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary">
                                {locationLabel}
                            </Typography>
                        </Box>
                    )}
                    {jobsCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <WorkIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary">
                                {jobLabel}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Description */}
                {company.description && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.5
                        }}
                    >
                        {Utils.truncateTextWithEllipsis
                            ? Utils.truncateTextWithEllipsis(company.description, 80)
                            : company.description}
                    </Typography>
                )}

                {/* Footer */}
                <Box sx={{ mt: 'auto', pt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    {jobsCount > 0 ? (
                        <Chip
                            label={jobLabel}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '0.72rem', height: 24 }}
                        />
                    ) : (
                        <Chip
                            label="No open jobs"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.72rem', height: 24, color: 'text.disabled', borderColor: 'divider' }}
                        />
                    )}
                    <Button
                        component={Link}
                        to={`/employer/${company.id}`}
                        variant="outlined"
                        size="small"
                        disableElevation
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.72rem',
                            py: 0.25,
                            px: 1.25,
                            borderRadius: 1
                        }}
                    >
                        View
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
}

export default CompanyItem;
